import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Leaf, Calendar, Truck, ArrowRight, MessageCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useCart } from "@/store/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_BASKET_IMAGE = "https://customer-assets.emergentagent.com/job_2ad418ae-721e-40fd-824f-e8019fae87b8/artifacts/6ngr2f4g_IMG-20230607-WA0001.jpg";
const PRODUCTS_IMAGE = "https://images.unsplash.com/photo-1754891144113-8a8d092a7dbf?crop=entropy&cs=srgb&fm=jpg&q=85";
const HANDS_IMAGE = "https://images.unsplash.com/photo-1627738670355-45970f19bcd9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwzfHx2ZWdldGFibGUlMjBiYXNrZXQlMjBkZWxpdmVyeSUyMGZhcm0lMjBmcmVzaHxlbnwwfHx8fDE3NzE2MjMxOTF8MA&ixlib=rb-4.1.0&q=85";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};

const baskets = [
  {
    name: "Cabaz Individual",
    matchKey: "individual",
    price: "16€",
    weight: "6 kg",
    description: "Perfeito para quem vive sozinho ou quer experimentar os nossos produtos. Uma seleção cuidada de legumes e frutas da época.",
    features: [
      "6 kg de produtos frescos",
      "Ideal para 1-2 pessoas",
      "Entrega semanal",
      "Produtos 100% da época"
    ],
    highlight: false,
    badge: null
  },
  {
    name: "Cabaz Familiar",
    matchKey: "familiar",
    price: "20€",
    weight: "9 kg",
    description: "O mais popular! Maior quantidade e variedade para alimentar toda a família com produtos frescos e saborosos.",
    features: [
      "9 kg de produtos frescos",
      "Ideal para 3-4 pessoas",
      "Entrega semanal",
      "Grande variedade de legumes e frutas"
    ],
    highlight: true,
    badge: "Mais Popular"
  },
  {
    name: "Encomenda à Lista",
    matchKey: null,
    price: "mín. 16€",
    weight: "Variável",
    description: "Escolha os produtos que pretende! Todas as semanas disponibilizamos uma lista de produtos frescos para encomendar à medida.",
    features: [
      "Lista semanal de produtos disponíveis",
      "Escolha personalizada",
      "Encomenda mínima de 16€",
      "Flexibilidade total"
    ],
    highlight: false,
    badge: "Personalizado"
  }
];

const seasonalProducts = [
  { name: "Tomate", season: "Verão" },
  { name: "Alface", season: "Todo o ano" },
  { name: "Couve", season: "Inverno" },
  { name: "Cenoura", season: "Todo o ano" },
  { name: "Cebola", season: "Todo o ano" },
  { name: "Batata", season: "Outono" },
  { name: "Feijão Verde", season: "Verão" },
  { name: "Abóbora", season: "Outono" },
  { name: "Pimento", season: "Verão" },
  { name: "Courgette", season: "Verão" },
  { name: "Brócolos", season: "Inverno" },
  { name: "Espinafres", season: "Primavera" }
];

export default function ProductsPage() {
  const navigate = useNavigate();
  const { add, setOpen } = useCart();
  const [busyKey, setBusyKey] = useState(null);

  const handleOrder = async (basket) => {
    // "Encomenda à Lista" — go straight to the store
    if (!basket.matchKey) {
      navigate("/loja");
      return;
    }
    setBusyKey(basket.matchKey);
    try {
      const { data } = await axios.get(`${API}/products`, { params: { category: "cabazes" } });
      const product = data.find((p) =>
        p.name.toLowerCase().includes(basket.matchKey.toLowerCase())
      );
      if (!product) {
        toast.info("Este cabaz ainda não está disponível na loja. Pode compor a sua encomenda à lista.");
        navigate("/loja");
        return;
      }
      add(product, 1);
      navigate("/loja");
      setTimeout(() => setOpen(true), 250);
    } catch {
      toast.error("Não foi possível abrir a loja agora.");
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div data-testid="products-page" className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center pt-20">
        <div className="absolute inset-0 z-0">
          <img
            src={HERO_BASKET_IMAGE}
            alt="Cabaz de produtos frescos"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#3A4A38]/90 via-[#3A4A38]/70 to-[#3A4A38]/50" />
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl text-white"
          >
            <span className="font-accent text-2xl text-[#D4C4A8]">
              Frescos da Horta
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold leading-tight mt-2 mb-6">
              Os Nossos Cabazes
            </h1>
            <p className="text-lg text-white/80">
              Descubra os nossos cabazes semanais de produtos frescos, cultivados
              com carinho e entregues diretamente em sua casa.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Concept Section */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="font-accent text-2xl text-[#1B93A4]">
                O Conceito
              </span>
              <h2 className="text-3xl lg:text-4xl font-semibold text-[#2C2C2C] mt-2 mb-6">
                Cabazes Semanais
              </h2>
              <div className="gold-line mb-6" />

              <div className="space-y-4 text-[#2C2C2C]/80 leading-relaxed">
                <p>
                  Os nossos cabazes são preparados semanalmente com os melhores
                  produtos da nossa horta. Cada semana é diferente, refletindo
                  a época e o que a terra nos oferece nesse momento.
                </p>
                <p>
                  Não vendemos apenas legumes – partilhamos uma forma de vida.
                  Ao escolher os nossos cabazes, está a apoiar a agricultura
                  local, a sustentabilidade e uma alimentação mais saudável.
                </p>
                <p>
                  Todos os produtos são colhidos frescos, no dia anterior à entrega,
                  garantindo o máximo de sabor e nutrientes.
                </p>
              </div>

              <div className="flex flex-wrap gap-4 mt-8">
                <div className="flex items-center gap-2 text-[#2C2C2C]">
                  <Leaf className="w-5 h-5 text-[#3A4A38]" />
                  <span className="text-sm font-medium">100% Local</span>
                </div>
                <div className="flex items-center gap-2 text-[#2C2C2C]">
                  <Calendar className="w-5 h-5 text-[#3A4A38]" />
                  <span className="text-sm font-medium">Entrega Semanal</span>
                </div>
                <div className="flex items-center gap-2 text-[#2C2C2C]">
                  <Truck className="w-5 h-5 text-[#3A4A38]" />
                  <span className="text-sm font-medium">Região de Braga</span>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="image-hover rounded-xl overflow-hidden"
            >
              <img
                src={PRODUCTS_IMAGE}
                alt="Variedade de produtos frescos"
                className="w-full h-[400px] lg:h-[500px] object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Baskets Section */}
      <section className="py-20 lg:py-28 bg-[#F3F1EB] noise-overlay">
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="font-accent text-2xl text-[#1B93A4]">
              Escolha o seu
            </span>
            <h2 className="text-3xl lg:text-4xl font-semibold text-[#2C2C2C] mt-2">
              Tipos de Cabazes
            </h2>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid md:grid-cols-3 gap-6"
          >
            {baskets.map((basket, index) => (
              <motion.div key={index} variants={fadeInUp}>
                <Card
                  className={`h-full card-hover relative ${
                    basket.highlight
                      ? "border-2 border-[#3A4A38] bg-white"
                      : "border-[#D4C4A8]/50 bg-white"
                  }`}
                  data-testid={`basket-card-${index}`}
                >
                  {basket.badge && (
                    <Badge
                      className={`absolute -top-3 left-6 ${
                        basket.highlight
                          ? "bg-[#3A4A38] text-white"
                          : "bg-[#1B93A4] text-white"
                      }`}
                    >
                      {basket.badge}
                    </Badge>
                  )}
                  <CardHeader className="pb-2">
                    <CardTitle className="text-xl text-[#2C2C2C]">
                      {basket.name}
                    </CardTitle>
                    <div className="flex items-baseline gap-2 mt-2">
                      <span className="text-3xl font-bold text-[#3A4A38]">{basket.price}</span>
                      <span className="text-sm text-[#2C2C2C]/60">/ {basket.weight}</span>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <p className="text-[#2C2C2C]/70 text-sm leading-relaxed">
                      {basket.description}
                    </p>

                    <ul className="space-y-3">
                      {basket.features.map((feature, fIndex) => (
                        <li key={fIndex} className="flex items-start gap-2">
                          <Check className="w-4 h-4 text-[#1B93A4] mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-[#2C2C2C]/80">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <Button
                      onClick={() => handleOrder(basket)}
                      disabled={busyKey === basket.matchKey}
                      data-testid={`basket-cta-${index}`}
                      className={`w-full ${basket.highlight ? "btn-primary" : "btn-secondary"}`}
                    >
                      {busyKey === basket.matchKey ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : null}
                      {basket.matchKey ? "Encomendar" : "Compor encomenda"}
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>

          {/* Availability Notice */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 p-6 bg-white rounded-xl border border-[#D4C4A8]/50 text-center"
          >
            <div className="flex items-center justify-center gap-2 mb-2">
              <MessageCircle className="w-5 h-5 text-[#1B93A4]" />
              <span className="font-medium text-[#2C2C2C]">
                Confirmar disponibilidade por mensagem
              </span>
            </div>
            <p className="text-sm text-[#2C2C2C]/70">
              Zona de entrega: Região de Braga e Vila Verde / entregas locais.
              Contacte-nos para confirmar a disponibilidade na sua área.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Seasonal Products Section */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-2 lg:order-1 image-hover rounded-xl overflow-hidden"
            >
              <img
                src={HANDS_IMAGE}
                alt="Colheita de produtos frescos"
                className="w-full h-[400px] lg:h-[500px] object-cover"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-1 lg:order-2"
            >
              <span className="font-accent text-2xl text-[#1B93A4]">
                Da Época
              </span>
              <h2 className="text-3xl lg:text-4xl font-semibold text-[#2C2C2C] mt-2 mb-6">
                Produtos Sazonais
              </h2>
              <div className="gold-line mb-6" />

              <p className="text-[#2C2C2C]/80 leading-relaxed mb-8">
                Na Quinta da Oliveirinha cultivamos uma grande variedade de produtos,
                sempre respeitando os ciclos naturais. Aqui estão alguns dos produtos
                que pode encontrar nos nossos cabazes ao longo do ano:
              </p>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {seasonalProducts.map((product, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 p-2 rounded-lg bg-[#F3F1EB]/50"
                    data-testid={`product-${index}`}
                  >
                    <div className="w-2 h-2 rounded-full bg-[#3A4A38]" />
                    <div>
                      <span className="text-sm font-medium text-[#2C2C2C]">
                        {product.name}
                      </span>
                      <span className="text-xs text-[#2C2C2C]/60 block">
                        {product.season}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              <p className="text-sm text-[#2C2C2C]/60 mt-6 italic">
                * A disponibilidade varia consoante a época e as condições meteorológicas.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-28 bg-[#3A4A38] text-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-2xl mx-auto"
          >
            <span className="font-accent text-2xl text-[#D4C4A8]">
              Pronto para experimentar?
            </span>
            <h2 className="text-3xl lg:text-4xl font-semibold mt-2 mb-6">
              Encomende o seu cabaz
            </h2>
            <p className="text-white/80 mb-8">
              Entre em contacto connosco para escolher o cabaz ideal para si
              e confirmar a disponibilidade de entrega na sua zona.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/loja" data-testid="products-final-cta">
                <Button className="bg-white text-[#3A4A38] hover:bg-white/90 rounded-full px-8 py-6 text-base font-medium">
                  Fazer Encomenda
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/como-funciona" data-testid="products-how-cta">
                <Button
                  variant="outline"
                  className="border-white text-white hover:bg-white/10 rounded-full px-8 py-6 text-base font-medium"
                >
                  Como Funciona
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
